import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedLine } from 'app/components/Common/Animation/AnimatedLine'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import React, { memo } from 'react'
import { Parallax } from 'react-scroll-parallax'

export interface Props {
  centerImage?: ImageProps
  cta?: ButtonProps
  description?: string
  leftImage?: ImageProps
  rightImage?: ImageProps
  subtitle?: string
  title?: string
}

export const MenuBlock = memo(function MenuBlock({
  centerImage,
  cta,
  description,
  leftImage,
  rightImage,
  subtitle,
  title,
}: Props) {
  if (!title) {
    return null
  }

  return (
    <Container>
      {title ? (
        <Head>
          <Title tag="h2" text={title} className="item-title" />
          {subtitle ? (
            <FadeInUp delay={0.4}>
              <Subtitle>{subtitle}</Subtitle>
            </FadeInUp>
          ) : null}
          <AnimatedLine variant="light" />
        </Head>
      ) : null}

      <Wrapper dial={2} row space="between">
        {leftImage ? (
          <LeftSide>
            <ImageWrap className="item-image">
              <DropDown>
                <Parallax scale={[1.4, 1]} translateY={['-20%', '20%']}>
                  <Image {...leftImage} />
                </Parallax>
              </DropDown>
            </ImageWrap>
          </LeftSide>
        ) : null}

        {centerImage ? (
          <CenterSide>
            <Text>
              {description ? (
                <FadeInUp delay={1}>
                  <Description
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </FadeInUp>
              ) : null}
              {cta ? <CTA variant="white" {...cta} /> : null}
            </Text>
            <ImageWrap className="item-image-center">
              <DropDown>
                <Parallax scale={[1.4, 1]} translateY={['-20%', '20%']}>
                  <Image {...centerImage} />
                </Parallax>
              </DropDown>
            </ImageWrap>
          </CenterSide>
        ) : null}

        {rightImage ? (
          <RightSide>
            <ImageWrap className="item-image">
              <DropDown>
                <Parallax scale={[1.4, 1]} translateY={['-20%', '20%']}>
                  <Image {...rightImage} />
                </Parallax>
              </DropDown>
            </ImageWrap>
          </RightSide>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled(FlexBox)`
  margin-top: 10.5rem;
  padding-bottom: 11.25rem;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    padding-bottom: 5.25rem;
  }
`

const Head = styled.div`
  text-align: center;
  width: 100%;

  .item-line {
    margin-top: 2.25rem;
  }
`

const Text = styled.div`
  text-align: center;
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  margin-bottom: 0.75rem;
`

const Subtitle = styled.div`
  ${styles.title.small}

  text-transform: none;
`

const Description = styled.div`
  ${styles.description.light}

  @media (max-width: 1199px) {
    margin: 1.5rem auto 0;
  }
`

const Wrapper = styled(FlexBox)`
  width: 100%;
  margin-top: 5.25rem;
  padding: 0 5.833vw;

  @media (max-width: 1199px) {
    margin-top: 0;
    padding: 3rem 3rem 0;
    flex-wrap: wrap;
  }
`

const Style = css`
  position: relative;
  overflow: hidden;
  width: 19.583vw;
  aspect-ratio: 0.76;

  > div {
    height: 100%;
  }

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    width: 100%;
    margin-top: 3rem;
  }
`

const ImageWrap = styled.div`
  ${Style}
`

const CenterSide = styled.div`
  width: 27.222vw;

  .item-image-center {
    width: 100%;
    aspect-ratio: 1.5;
    margin-top: 5.25rem;
  }

  @media (max-width: 1199px) {
    width: 100%;

    .item-image-center {
      margin-top: 3rem;
    }
  }
`

const LeftSide = styled.div`
  margin-top: 5rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
    order: 1;
    width: calc(50% - 0.75rem);

    .item-image {
      width: 100%;
      margin-top: 0;
    }
  }
`

const RightSide = styled.div`
  margin-top: 5rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
    width: calc(50% - 0.75rem);

    .item-image {
      width: 100%;
      margin-top: 0;
    }
  }
`

const CTA = styled(Button)`
  margin-top: 3rem;
`
